// Expenses management hooks
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { queryKeys } from '@/lib/query-client'
import { isAssistantLoggedIn } from '@/lib/permissions'
import type { Expense } from '@/types/entities'

export interface ExpenseFilters {
  startDate?: string
  endDate?: string
  categoryId?: string
  limit?: number
  offset?: number
  includeDeleted?: boolean
}

// Extended Expense type with audit display fields
export interface ExpenseWithAudit extends Expense {
  deletedAt?: string
  updatedAt?: string
  createdByAssistantId?: string
  updatedBy?: string
  updatedByAssistantId?: string
  createdByDisplay?: string
  updatedByDisplay?: string
}

/**
 * Fetch expenses for a restaurant with optional filters
 * Automatically uses assistant RPC if logged in as assistant
 */
export function useExpenses(restaurantId: string | null, filters?: ExpenseFilters) {
  return useQuery({
    queryKey: queryKeys.accounting.expenses(restaurantId || '', filters as Record<string, unknown> | undefined),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    queryFn: async (): Promise<{ expenses: ExpenseWithAudit[]; totalCount: number }> => {
      if (!restaurantId) {
        return { expenses: [], totalCount: 0 }
      }

      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        const { data, error } = await supabase.rpc('assistant_get_expenses' as any, {
          p_restaurant_id: restaurantId,
          p_start_date: (filters?.startDate || null) as any,
          p_end_date: (filters?.endDate || null) as any,
          p_category_id: (filters?.categoryId || null) as any,
          p_limit: filters?.limit ?? 100,
          p_offset: filters?.offset ?? 0,
          p_include_deleted: filters?.includeDeleted ?? false,
        })

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            console.warn('Assistant does not have permission to view expenses')
            return { expenses: [], totalCount: 0 }
          }
          throw error
        }

        const expenses = (data || []).map((e: any) => ({
          id: e.id,
          restaurantId: e.restaurant_id,
          categoryId: e.category_id,
          recurringExpenseId: e.recurring_expense_id ?? undefined,
          employeeId: e.employee_id ?? undefined,
          expenseDate: e.expense_date,
          amount: Number(e.amount),
          descriptionAr: e.description_ar ?? undefined,
          descriptionFr: e.description_fr ?? undefined,
          referenceNumber: e.reference_number ?? undefined,
          notes: e.notes ?? undefined,
          createdAt: e.created_at,
          createdBy: e.created_by ?? undefined,
          deletedAt: e.deleted_at ?? undefined,
          updatedAt: e.updated_at ?? undefined,
          createdByAssistantId: e.created_by_assistant_id ?? undefined,
          updatedBy: e.updated_by ?? undefined,
          updatedByAssistantId: e.updated_by_assistant_id ?? undefined,
          categoryNameAr: e.category_name_ar,
          categoryNameFr: e.category_name_fr,
          categoryIcon: e.category_icon ?? undefined,
          categoryColor: e.category_color ?? undefined,
          employeeNameAr: e.employee_name_ar ?? undefined,
          employeeNameFr: e.employee_name_fr ?? undefined,
          createdByDisplay: e.created_by_display ?? undefined,
          updatedByDisplay: e.updated_by_display ?? undefined,
          totalCount: Number(e.total_count),
        }))

        return {
          expenses,
          totalCount: expenses.length > 0 ? (expenses[0]?.totalCount ?? 0) : 0,
        }
      }

      // Admin flow
      const { data, error } = await supabase.rpc('get_expenses', {
        p_restaurant_id: restaurantId,
        p_start_date: (filters?.startDate || null) as any,
        p_end_date: (filters?.endDate || null) as any,
        p_category_id: (filters?.categoryId || null) as any,
        p_limit: filters?.limit ?? 100,
        p_offset: filters?.offset ?? 0,
        p_include_deleted: filters?.includeDeleted ?? false,
      })

      if (error) {
        throw error
      }

      const expenses = (data || []).map((e: any) => ({
        id: e.id,
        restaurantId: e.restaurant_id,
        categoryId: e.category_id,
        recurringExpenseId: e.recurring_expense_id ?? undefined,
        employeeId: e.employee_id ?? undefined,
        expenseDate: e.expense_date,
        amount: Number(e.amount),
        descriptionAr: e.description_ar ?? undefined,
        descriptionFr: e.description_fr ?? undefined,
        referenceNumber: e.reference_number ?? undefined,
        notes: e.notes ?? undefined,
        createdAt: e.created_at,
        createdBy: e.created_by ?? undefined,
        deletedAt: e.deleted_at ?? undefined,
        updatedAt: e.updated_at ?? undefined,
        createdByAssistantId: e.created_by_assistant_id ?? undefined,
        updatedBy: e.updated_by ?? undefined,
        updatedByAssistantId: e.updated_by_assistant_id ?? undefined,
        categoryNameAr: e.category_name_ar,
        categoryNameFr: e.category_name_fr,
        categoryIcon: e.category_icon ?? undefined,
        categoryColor: e.category_color ?? undefined,
        employeeNameAr: e.employee_name_ar ?? undefined,
        employeeNameFr: e.employee_name_fr ?? undefined,
        createdByDisplay: e.created_by_display ?? undefined,
        updatedByDisplay: e.updated_by_display ?? undefined,
        totalCount: Number(e.total_count),
      }))

      return {
        expenses,
        totalCount: expenses.length > 0 ? (expenses[0]?.totalCount ?? 0) : 0,
      }
    },
    enabled: !!restaurantId,
  })
}

/**
 * Create a new expense
 * Automatically uses assistant RPC if logged in as assistant
 */
export function useCreateExpense(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: {
      categoryId: string
      expenseDate: string
      amount: number
      descriptionAr?: string
      descriptionFr?: string
      recurringExpenseId?: string
      employeeId?: string
      referenceNumber?: string
      notes?: string
    }) => {
      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        const { data, error } = await supabase.rpc('assistant_create_expense' as any, {
          p_restaurant_id: restaurantId,
          p_category_id: request.categoryId,
          p_expense_date: request.expenseDate,
          p_amount: request.amount,
          p_description_ar: (request.descriptionAr || null) as any,
          p_description_fr: (request.descriptionFr || null) as any,
          p_recurring_expense_id: (request.recurringExpenseId || null) as any,
          p_reference_number: (request.referenceNumber || null) as any,
          p_notes: (request.notes || null) as any,
        })

        if (error) {
          throw error
        }

        return data
      }

      // Admin flow
      const { data, error } = await supabase.rpc('create_expense', {
        p_restaurant_id: restaurantId,
        p_category_id: request.categoryId,
        p_expense_date: request.expenseDate,
        p_amount: request.amount,
        p_description_ar: (request.descriptionAr || null) as any,
        p_description_fr: (request.descriptionFr || null) as any,
        p_recurring_expense_id: (request.recurringExpenseId || null) as any,
        p_employee_id: (request.employeeId || null) as any,
        p_reference_number: (request.referenceNumber || null) as any,
        p_notes: (request.notes || null) as any,
      })

      if (error) {
        throw error
      }

      return data
    },
    onSuccess: () => {
      // Invalidate all expense-related queries for this restaurant
      queryClient.invalidateQueries({
        queryKey: ['accounting', 'expenses', restaurantId],
      })
      queryClient.invalidateQueries({
        queryKey: ['accounting', 'financial-summary', restaurantId],
      })
      queryClient.invalidateQueries({
        queryKey: ['accounting', 'expense-breakdown', restaurantId],
      })
    },
  })
}

/**
 * Update an existing expense
 * Automatically uses assistant RPC if logged in as assistant
 */
export function useUpdateExpense(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: {
      expenseId: string
      categoryId: string
      expenseDate: string
      amount: number
      descriptionAr?: string
      descriptionFr?: string
      referenceNumber?: string
      notes?: string
    }) => {
      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        const { data, error } = await supabase.rpc('assistant_update_expense' as any, {
          p_restaurant_id: restaurantId,
          p_expense_id: request.expenseId,
          p_category_id: request.categoryId,
          p_expense_date: request.expenseDate,
          p_amount: request.amount,
          p_description_ar: (request.descriptionAr || null) as any,
          p_description_fr: (request.descriptionFr || null) as any,
          p_reference_number: (request.referenceNumber || null) as any,
          p_notes: (request.notes || null) as any,
        })

        if (error) {
          throw error
        }

        return data
      }

      // Admin flow
      const { data, error } = await supabase.rpc('update_expense', {
        p_expense_id: request.expenseId,
        p_category_id: request.categoryId,
        p_expense_date: request.expenseDate,
        p_amount: request.amount,
        p_description_ar: (request.descriptionAr || null) as any,
        p_description_fr: (request.descriptionFr || null) as any,
        p_reference_number: (request.referenceNumber || null) as any,
        p_notes: (request.notes || null) as any,
      })

      if (error) {
        throw error
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['accounting', 'expenses', restaurantId],
      })
      queryClient.invalidateQueries({
        queryKey: ['accounting', 'financial-summary', restaurantId],
      })
      queryClient.invalidateQueries({
        queryKey: ['accounting', 'expense-breakdown', restaurantId],
      })
    },
  })
}

/**
 * Delete an expense
 * Automatically uses assistant RPC if logged in as assistant
 */
export function useDeleteExpense(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (expenseId: string) => {
      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        const { data, error } = await supabase.rpc('assistant_delete_expense' as any, {
          p_restaurant_id: restaurantId,
          p_expense_id: expenseId,
        })

        if (error) {
          throw error
        }

        return data
      }

      // Admin flow
      const { data, error } = await supabase.rpc('delete_expense', {
        p_expense_id: expenseId,
      })

      if (error) {
        throw error
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['accounting', 'expenses', restaurantId],
      })
      queryClient.invalidateQueries({
        queryKey: ['accounting', 'financial-summary', restaurantId],
      })
      queryClient.invalidateQueries({
        queryKey: ['accounting', 'expense-breakdown', restaurantId],
      })
    },
  })
}

/**
 * Fetch expense edit history
 * @param isSalaryPayment - If true, uses salaries.view permission instead of expenses.view for assistants
 */
export function useExpenseEditHistory(
  restaurantId: string | null,
  expenseId: string | null,
  isSalaryPayment: boolean = false
) {
  return useQuery({
    queryKey: ['accounting', 'expense-edit-history', restaurantId, expenseId, isSalaryPayment],
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    queryFn: async () => {
      if (!restaurantId || !expenseId) {
        return []
      }

      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        // Use appropriate RPC based on whether it's a salary payment
        const rpcName = isSalaryPayment
          ? 'assistant_get_salary_payment_edit_history'
          : 'assistant_get_expense_edit_history'

        const { data, error } = await supabase.rpc(rpcName as any, {
          p_restaurant_id: restaurantId,
          p_expense_id: expenseId,
        })

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            console.warn('Assistant does not have permission to view expense edit history')
            return []
          }
          throw error
        }

        return (data || []).map((h: any) => ({
          id: h.id,
          expenseId: h.expense_id,
          restaurantId: h.restaurant_id,
          fieldChanged: h.field_changed,
          oldValue: h.old_value,
          newValue: h.new_value,
          editedAt: h.edited_at,
          editedBy: h.edited_by,
          editedByAssistantId: h.edited_by_assistant_id,
          createdAt: h.created_at,
          modifiedBy: h.modified_by,
        }))
      }

      // Admin flow
      const { data, error } = await supabase.rpc('get_expense_edit_history' as any, {
        p_expense_id: expenseId,
      })

      if (error) {
        throw error
      }

      return ((data as any[]) || []).map((h: any) => ({
        id: h.id,
        expenseId: h.expense_id,
        restaurantId: h.restaurant_id,
        fieldChanged: h.field_changed,
        oldValue: h.old_value,
        newValue: h.new_value,
        editedAt: h.edited_at,
        editedBy: h.edited_by,
        editedByAssistantId: h.edited_by_assistant_id,
        createdAt: h.created_at,
        modifiedBy: h.modified_by,
      }))
    },
    enabled: !!restaurantId && !!expenseId,
  })
}

// Salary payment type (for employees tab)
export interface SalaryPaymentExpense {
  id: string
  restaurantId: string
  categoryId: string
  employeeId: string
  expenseDate: string
  amount: number
  descriptionAr?: string
  descriptionFr?: string
  notes?: string
  createdAt: string
  deletedAt?: string
  updatedAt?: string
  createdByAssistantId?: string
  updatedByAssistantId?: string
  employeeNameAr?: string
  employeeNameFr?: string
  createdByDisplay?: string
  updatedByDisplay?: string
}

/**
 * Fetch salary payments (expenses with employeeId)
 * For assistants, uses assistant_get_salary_payments RPC
 * For admins, uses get_expenses with employee filter
 */
export function useSalaryPayments(
  restaurantId: string | null,
  filters?: {
    startDate?: string
    endDate?: string
    includeDeleted?: boolean
  }
) {
  return useQuery({
    queryKey: ['accounting', 'salary-payments', restaurantId, filters],
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    queryFn: async (): Promise<SalaryPaymentExpense[]> => {
      if (!restaurantId) {
        return []
      }

      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        const { data, error } = await supabase.rpc('assistant_get_salary_payments' as any, {
          p_restaurant_id: restaurantId,
          p_start_date: (filters?.startDate || null) as any,
          p_end_date: (filters?.endDate || null) as any,
          p_include_deleted: filters?.includeDeleted ?? false,
        })

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            console.warn('Assistant does not have permission to view salary payments')
            return []
          }
          throw error
        }

        return (data || []).map((e: any) => ({
          id: e.id,
          restaurantId: e.restaurant_id,
          categoryId: e.category_id,
          employeeId: e.employee_id,
          expenseDate: e.expense_date,
          amount: Number(e.amount),
          descriptionAr: e.description_ar ?? undefined,
          descriptionFr: e.description_fr ?? undefined,
          notes: e.notes ?? undefined,
          createdAt: e.created_at,
          deletedAt: e.deleted_at ?? undefined,
          updatedAt: e.updated_at ?? undefined,
          createdByAssistantId: e.created_by_assistant_id ?? undefined,
          updatedByAssistantId: e.updated_by_assistant_id ?? undefined,
          employeeNameAr: e.employee_name_ar ?? undefined,
          employeeNameFr: e.employee_name_fr ?? undefined,
          createdByDisplay: e.created_by_display ?? undefined,
          updatedByDisplay: e.updated_by_display ?? undefined,
        }))
      }

      // Admin flow - use get_expenses with large limit and filter by employeeId
      const { data, error } = await supabase.rpc('get_expenses', {
        p_restaurant_id: restaurantId,
        p_start_date: (filters?.startDate || null) as any,
        p_end_date: (filters?.endDate || null) as any,
        p_category_id: null as any,
        p_limit: 1000,
        p_offset: 0,
        p_include_deleted: filters?.includeDeleted ?? false,
      })

      if (error) {
        throw error
      }

      // Filter to only salary payments (expenses with employee_id)
      return ((data as any[]) || [])
        .filter((e: any) => e.employee_id)
        .map((e: any) => ({
          id: e.id,
          restaurantId: e.restaurant_id,
          categoryId: e.category_id,
          employeeId: e.employee_id,
          expenseDate: e.expense_date,
          amount: Number(e.amount),
          descriptionAr: e.description_ar ?? undefined,
          descriptionFr: e.description_fr ?? undefined,
          notes: e.notes ?? undefined,
          createdAt: e.created_at,
          deletedAt: e.deleted_at ?? undefined,
          updatedAt: e.updated_at ?? undefined,
          createdByAssistantId: e.created_by_assistant_id ?? undefined,
          updatedByAssistantId: e.updated_by_assistant_id ?? undefined,
          employeeNameAr: e.employee_name_ar ?? undefined,
          employeeNameFr: e.employee_name_fr ?? undefined,
          createdByDisplay: e.created_by_display ?? undefined,
          updatedByDisplay: e.updated_by_display ?? undefined,
        }))
    },
    enabled: !!restaurantId,
  })
}

/**
 * Create a salary payment (expense with employeeId)
 * For assistants, uses assistant_create_salary_payment RPC
 * For admins, uses create_expense with employeeId
 */
export function useCreateSalaryPayment(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: {
      employeeId: string
      amount: number
      expenseDate: string
      descriptionAr?: string
      descriptionFr?: string
      notes?: string
    }) => {
      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        const { data, error } = await supabase.rpc('assistant_create_salary_payment' as any, {
          p_restaurant_id: restaurantId,
          p_employee_id: request.employeeId,
          p_amount: request.amount,
          p_expense_date: request.expenseDate,
          p_description_ar: (request.descriptionAr || null) as any,
          p_description_fr: (request.descriptionFr || null) as any,
          p_notes: (request.notes || null) as any,
        })

        if (error) {
          throw error
        }

        return data
      }

      // Admin flow - get salary category first
      const { data: categories } = await supabase.rpc('get_expense_categories', {
        p_restaurant_id: restaurantId,
      })

      const salaryCategory = (categories as any[])?.find(
        (c: any) => c.icon === 'users' && c.is_system === true
      )

      if (!salaryCategory) {
        throw new Error('Salary category not found')
      }

      const { data, error } = await supabase.rpc('create_expense', {
        p_restaurant_id: restaurantId,
        p_category_id: salaryCategory.id,
        p_expense_date: request.expenseDate,
        p_amount: request.amount,
        p_description_ar: (request.descriptionAr || null) as any,
        p_description_fr: (request.descriptionFr || null) as any,
        p_recurring_expense_id: null as any,
        p_employee_id: request.employeeId,
        p_reference_number: null as any,
        p_notes: (request.notes || null) as any,
      })

      if (error) {
        throw error
      }

      return data
    },
    onSuccess: () => {
      // Invalidate salary payment queries
      queryClient.invalidateQueries({
        queryKey: ['accounting', 'salary-payments', restaurantId],
      })
      queryClient.invalidateQueries({
        queryKey: ['accounting', 'expenses', restaurantId],
      })
      queryClient.invalidateQueries({
        queryKey: ['accounting', 'financial-summary', restaurantId],
      })
      queryClient.invalidateQueries({
        queryKey: ['accounting', 'expense-breakdown', restaurantId],
      })
    },
  })
}
